import {
  ColorClass,
  DropDownButton,
  DropDownButtonDirection,
  Icon,
  IconIdentifier,
  Placement,
  Tooltip,
  UploadStatus,
  UploadWidget,
} from '@aus-platform/design-system';
import { useNetworkState } from '@uidotdev/usehooks';
import { isEmpty, isNil } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  selectMap3dWorkspace,
  setShowUnsavedFeaturesModal,
} from 'map-3d/shared/map-3d-slices';
import { useCompleteUpload, useMyOrg } from 'shared/api';
import { ConfirmationModal } from 'shared/components';
import { history } from 'shared/constants';
import { GlobalContext } from 'shared/context';
import { FeatureFlag } from 'shared/enums';
import { UserLocalDataManager } from 'shared/helpers/local-storage-managers/user-manager';
import { cancelPendingUploads } from 'shared/hooks';
import {
  resetUploadWidget,
  selectUploadWidget,
  setIsFilesUploadWidgetOpen,
  UploadItemKind,
} from 'shared/upload-widget-slices';
import { CustomDate } from 'shared/utils';
import { Image } from 'src/assets';
import { ENFORCEMENT_MODAL_KEY, TwoFactorEnforcementModal } from 'src/auth';
import {
  PASSWORD_ENFORCEMENT_MODAL_KEY,
  PasswordEnforcementModal,
} from 'src/auth/change-password';
import type { SideCardType } from '../accordion-sidenav/types';
import { UploadWidgetFileSet } from './upload-widget-file-set';
import { UploadWidgetFiles } from './upload-widget-files';

type HeaderType = {
  onClick: () => void;
  onClose: () => void;
  showSideCard: SideCardType;
  openSettingsModal: () => void;
};

const badgeStatus = {
  Idle: 'Idle',
  Uploading: 'Uploading',
};

// Constants.
const PASSWORD_EXPIRY_THRESHOLD = 24 * 60;

export const Header: React.FC<HeaderType> = ({
  onClick,
  onClose,
  showSideCard,
  openSettingsModal,
}) => {
  // Hooks.
  const { loggedUser, setLoggedUser } = useContext(GlobalContext);
  const { mutate: completeUpload } = useCompleteUpload();
  const { online } = useNetworkState();

  // Dispatchers.
  const dispatch = useAppDispatch();

  // Refs.
  const uploadWidgetRef = useRef<HTMLDivElement>(null);
  const widgetboxRef = useRef<HTMLInputElement>(null);

  // Selectors.
  const { drawingTools } = useAppSelector(selectMap3dWorkspace);
  const { isFilesUploadWidgetOpen, filesToUpload, uploadStatusList } =
    useAppSelector(selectUploadWidget);

  // Variables.
  const uploadingCount = uploadStatusList.filter((uploadItem) =>
    [
      UploadStatus.InProgress,
      UploadStatus.Paused,
      UploadStatus.Pending,
      UploadStatus.NetworkError,
    ].includes(uploadItem.uploadStatus),
  ).length;

  const is2FAEnforcedAndIncomplete =
    loggedUser?.featureFlags?.[FeatureFlag.TwoFactorAuthentication] &&
    !loggedUser?.is2FAConfigured;

  const is2FAEnforcementModalShown =
    localStorage.getItem(ENFORCEMENT_MODAL_KEY) === 'true';

  const passwordExpiresAt = loggedUser?.passwordExpiresAt ?? null;

  const isPasswordEnforcementModalShown =
    localStorage.getItem(PASSWORD_ENFORCEMENT_MODAL_KEY) === 'true';

  const minutesRemainingForPasswordExpiry = passwordExpiresAt
    ? new CustomDate(passwordExpiresAt)?.getRemainingMinutes()
    : undefined;

  const shouldShowPasswordEnforcementModal =
    !isNil(minutesRemainingForPasswordExpiry) &&
    minutesRemainingForPasswordExpiry <= PASSWORD_EXPIRY_THRESHOLD &&
    minutesRemainingForPasswordExpiry > 0;

  //States.
  const [isCancelledAllUploads, setIsCancelledAllUploads] =
    useState<boolean>(false);
  const [showConfirmAllUploadsModal, setShowConfirmAllUploadsModal] =
    useState(false);
  const [currentBadgeStatus, setCurrentBadgeStatus] = useState(
    badgeStatus.Idle,
  );
  const [isEnforcementModalOpen, setIsEnforcementModalOpen] = useState<boolean>(
    !is2FAEnforcementModalShown,
  );
  const [isPasswordEnforcementModalOpen, setIsPasswordEnforcementModalOpen] =
    useState(!isPasswordEnforcementModalShown);

  // Api.
  const { data: myOrgResponse } = useMyOrg();

  //useEffects.
  useEffect(() => {
    if (uploadStatusList.length === 0) {
      setCurrentBadgeStatus(badgeStatus.Idle);
    }

    if (uploadingCount > 0) {
      setCurrentBadgeStatus(badgeStatus.Uploading);
    }
  }, [uploadingCount, uploadStatusList]);

  useEffect(() => {
    if (isCancelledAllUploads) {
      setIsCancelledAllUploads(false);
    }
  }, [isFilesUploadWidgetOpen, isCancelledAllUploads]);

  // Handle browser warning when files are uploading.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasActiveUploads = uploadStatusList.some((file) =>
        [
          UploadStatus.Pending,
          UploadStatus.InProgress,
          UploadStatus.Paused,
          UploadStatus.NetworkError,
        ].includes(file.uploadStatus),
      );

      if (hasActiveUploads) {
        const message =
          'You have files currently uploading. Are you sure you want to leave?';
        e.preventDefault();
        e.returnValue = message; // Still needed for Chrome
        return message; // Required for other browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [uploadStatusList]);

  // HACK: To cancel pending file uploads.
  useEffect(() => {
    cancelPendingUploads(completeUpload);
  }, []);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        dispatch(setIsFilesUploadWidgetOpen(false));
        setShowConfirmAllUploadsModal(false);
      }
    };

    const handleClickOutside = (event) => {
      if (
        !uploadWidgetRef.current?.parentElement?.contains(event.target) &&
        !event.target.closest('.modal-content')
      ) {
        dispatch(setIsFilesUploadWidgetOpen(false));
        setShowConfirmAllUploadsModal(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handlers.
  const handleLogout = () => {
    dispatch(resetUploadWidget());
    setLoggedUser(null);
    cancelPendingUploads(completeUpload);
    history.push('/logout');
  };

  // Prevent logout and redirection to / when there are unsaved features.
  const onLogoutBtnClick = () => {
    // Check for active uploads
    const hasActiveUploads = uploadStatusList.some((file) =>
      [
        UploadStatus.Pending,
        UploadStatus.InProgress,
        UploadStatus.Paused,
        UploadStatus.NetworkError,
      ].includes(file.uploadStatus),
    );

    if (hasActiveUploads) {
      if (
        window.confirm(
          'You have files currently uploading. Are you sure you want to leave?',
        )
      ) {
        handleLogout();
      }
      return;
    }

    if (drawingTools?.checkUnsavedFeatures()) {
      dispatch(setShowUnsavedFeaturesModal(true));
      return;
    }

    handleLogout();
  };

  const onLogoClick = () => {
    if (drawingTools?.checkUnsavedFeatures()) {
      dispatch(setShowUnsavedFeaturesModal(true));
      return;
    }
    history.push('/');
  };

  const onClickBadgeIcon = () => {
    dispatch(setIsFilesUploadWidgetOpen(!isFilesUploadWidgetOpen));
  };

  const handleCancelAllUploads = () => {
    setShowConfirmAllUploadsModal(true);
  };

  const handleConfirmCancelAllUploads = () => {
    setIsCancelledAllUploads(true);
    setShowConfirmAllUploadsModal(false);
  };

  const onCloseEnforcementModal = () => {
    localStorage.setItem(ENFORCEMENT_MODAL_KEY, 'true');
    setIsEnforcementModalOpen(false);
  };

  const onClosePasswordEnforcementModal = () => {
    localStorage.setItem(PASSWORD_ENFORCEMENT_MODAL_KEY, 'true');
    setIsPasswordEnforcementModalOpen(false);
  };

  // Render.
  return (
    <div className="header">
      {showSideCard.primary ? (
        <Icon
          identifier={IconIdentifier.Cross}
          size={32}
          cursor={true}
          className="header__navbar"
          onClick={() => onClose()}
        />
      ) : (
        <Icon
          identifier={IconIdentifier.HamburgerMenu}
          size={32}
          cursor={true}
          className="header__navbar"
          onClick={() => onClick()}
        />
      )}
      <div onClick={onLogoClick} className="header__title">
        <img
          className="header__title__logo"
          alt="Aereo Logo"
          src={Image.AereoCloudLogoWhite}
          draggable={false}
        />
      </div>

      <div className="header-container" id="header-container">
        <div
          className={`header-container__badge ${isFilesUploadWidgetOpen ? 'active-state' : ''}`}
        >
          <Tooltip
            hoverText={
              uploadingCount
                ? `Uploading ${uploadingCount} item${uploadingCount === 1 ? '' : 's'}`
                : 'No uploads in progress'
            }
            className="header-container__badge__tooltip"
            placement={Placement.Bottom}
          >
            <div
              className="header-container__badge__icon-container"
              onClick={onClickBadgeIcon}
            >
              <Icon
                badgeProps={
                  currentBadgeStatus === badgeStatus.Idle
                    ? undefined
                    : {
                        text: uploadingCount > 99 ? '99+' : `${uploadingCount}`,
                        bgColorClass: uploadingCount
                          ? ColorClass.Warning500
                          : ColorClass.Success500,
                        iconIdentifier: uploadingCount
                          ? undefined
                          : IconIdentifier.CheckSmall,
                      }
                }
                identifier={IconIdentifier.Unarchive}
                colorClass={ColorClass.White}
                size={24}
              />
            </div>
          </Tooltip>

          <div ref={uploadWidgetRef}>
            <UploadWidget
              ref={widgetboxRef}
              uploadItems={uploadStatusList}
              onCancelAllUploads={handleCancelAllUploads}
              className={
                !isFilesUploadWidgetOpen ? 'upload-widget__hidden' : ''
              }
            >
              {isEmpty(filesToUpload) ? (
                <div className="upload-widget__no-uploads">
                  <Icon
                    identifier={IconIdentifier.FolderOff}
                    size={56}
                    colorClass={ColorClass.Neutral200}
                  />
                  <div>No uploads in progress</div>
                </div>
              ) : (
                filesToUpload.map((uploadItem) =>
                  uploadItem.kind === UploadItemKind.FileSet ? (
                    <UploadWidgetFileSet
                      uploadItem={uploadItem}
                      isCancelledAllUploads={isCancelledAllUploads}
                      setIsCancelledAllUploads={setIsCancelledAllUploads}
                      key={uploadItem.id}
                    />
                  ) : (
                    <UploadWidgetFiles
                      uploadItem={uploadItem}
                      isCancelledAllUploads={isCancelledAllUploads}
                      setIsCancelledAllUploads={setIsCancelledAllUploads}
                      online={online}
                      key={uploadItem.id}
                    />
                  ),
                )
              )}
            </UploadWidget>
          </div>
        </div>
        <div className="header-container__dropdown">
          <DropDownButton
            btnText={UserLocalDataManager.getUserName()?.toString()}
            separator={true}
            rightIconIdentifier={IconIdentifier.ChevronSmallDown}
            btnProps={{ className: 'header-container__dropdown-btn' }}
            onClick={() => onClose()}
            drop={DropDownButtonDirection.Down}
            data-testid="test-dropdown-id"
          >
            <DropDownButton.Item
              className="header-container__dropdown__item"
              onClick={openSettingsModal}
            >
              Settings
            </DropDownButton.Item>
            <DropDownButton.Item
              className="header-container__dropdown__item logout"
              onClick={onLogoutBtnClick}
            >
              Log Out
            </DropDownButton.Item>
          </DropDownButton>
        </div>
      </div>

      {showConfirmAllUploadsModal && (
        <ConfirmationModal
          className="cancel-all-modal"
          title="Cancel All Uploads?"
          isConfirmDanger
          containerRef={widgetboxRef}
          onClose={() => setShowConfirmAllUploadsModal(false)}
          cancelText="Continue Uploads"
          confirmText="Cancel Uploads"
          onSubmit={handleConfirmCancelAllUploads}
          message="Uploads are not complete. Would you like to cancel all ongoing uploads?"
        />
      )}

      {/* Enforcement Modal for Password Change. */}
      {shouldShowPasswordEnforcementModal && isPasswordEnforcementModalOpen && (
        <PasswordEnforcementModal
          onClose={onClosePasswordEnforcementModal}
          passwordExpiresAt={passwordExpiresAt}
        />
      )}

      {/* Enforcement Modal for 2FA. */}
      {!isPasswordEnforcementModalOpen &&
        is2FAEnforcedAndIncomplete &&
        isEnforcementModalOpen && (
          <TwoFactorEnforcementModal
            gracePeriodDeadline={myOrgResponse?.data?.gracePeriodFor2FA ?? null}
            onClose={onCloseEnforcementModal}
          />
        )}
    </div>
  );
};
